import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateModule } from '@ngx-translate/core';
import { DialogRef } from '@ngneat/dialog';
import { untilDestroyed } from '@ngneat/until-destroy';

import { NewsEntity } from '@core/entities';
import { ApprovedStatus, NewsType, NotificationType, SocketEvents } from '@core/enums';
import { getAlertTypeByApprovedStatusOfEntity, GetUserId, isMobileView } from '@core/utils';

import { AlertMessageComponent, BadgeComponent, ButtonComponent, IconComponent, TabsSelectionComponent } from '@shared/components/ui';
import { DefaultImageDirective, FetchProfileImageDirective } from '@shared/directives';
import { NewsFormComponent } from '@shared/components/forms/news-form/news-form.component';
import { NewsComposition } from '@shared/compositions/news.composition';
import { HumanizePipe, TruncateTextPipe } from '@shared/pipes';

@Component({
  selector: 'app-news-details',
  imports: [
    CommonModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
    DefaultImageDirective,
    FetchProfileImageDirective,
    IconComponent,
    BadgeComponent,
    AlertMessageComponent,
    HumanizePipe,
    TruncateTextPipe,
    TabsSelectionComponent,
    ButtonComponent,
  ],
  templateUrl: './news-details.component.html',
  styleUrls: ['./news-details.component.scss'],
})
export class NewsDetailsComponent extends NewsComposition implements OnInit {
  selectedNews: NewsEntity;

  bottomSheetData: any = {};
  currentIndex = 0;
  protected readonly isMobileView = isMobileView;
  protected readonly NewsType = NewsType;
  protected readonly ApprovedStatus = ApprovedStatus;
  private readonly _dialogRef: DialogRef;

  constructor() {
    super();
    if (!isMobileView()) {
      this._dialogRef = inject(DialogRef);
    }
  }

  get alertType() {
    return getAlertTypeByApprovedStatusOfEntity(this.originalRecord);
  }

  get isFirstRecord(): boolean {
    return this.currentIndex === 0;
  }

  get isLastRecord(): boolean {
    return this.currentIndex === this.newsList.length - 1;
  }

  get isUserOwner(): boolean {
    return this.currentNews?.user?.id === +GetUserId();
  }

  get showUpdatedRecord(): boolean {
    return this.originalRecord?.updatedRecord && !(this.originalRecord?.denyById && this.originalRecord?.denyByUser);
  }

  ngOnInit() {
    if (isMobileView()) {
      this.currentNews = this._bottomSheetService.currentSheetData.news || new NewsEntity();
      this.newsList = this._bottomSheetService.currentSheetData?.newsList || [];
    } else {
      this.currentNews = this._dialogRef?.data?.news || new NewsEntity();
      this.newsList = this._dialogRef.data.newsList || [];
      if (this.newsList && this.newsList.length > 0) {
        this.currentIndex = this.newsList.findIndex((news) => news.id === this.currentNews.id);
        this.selectedNews = this.newsList[this.currentIndex];
      }
    }

    if (this.currentNews && this.currentNews.id) {
      this._fetchNewsById(this.currentNews.id, false);

      this._socketService
        .fromEvent(SocketEvents.GetNotification)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (data: NotificationType) => {
            if (data === NotificationType.News) {
              this._fetchNewsById(this.currentNews.id, false);
            }
          },
        });
    }
  }

  closeDialog() {
    this._dialogRef.close();
  }

  editNewsDialog() {
    this._dialogService.open(NewsFormComponent, {
      size: 'md',
      closeButton: false,
      windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
      data: {
        news: this.currentNews,
      },
    });
  }

  loadPrevNews() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.currentNews = this.newsList[this.currentIndex];
      if (this.currentNews && this.currentNews.id) {
        this._fetchNewsById(this.currentNews.id, false);
      }
    } else {
      console.log('No previous News available');
    }
  }

  loadNextNews() {
    if (this.currentIndex < this.newsList.length - 1) {
      this.currentIndex++;
      this.currentNews = this.newsList[this.currentIndex];
      if (this.currentNews && this.currentNews.id) {
        this._fetchNewsById(this.currentNews.id, false);
      }
    } else {
      console.log('No next news available');
    }
  }

  editNewsBottomSheet(news: NewsEntity) {
    this._bottomSheetService.open(NewsFormComponent, {
      height: '95%',
      showCloseButton: false,
      data: {
        news,
        newsList: this.newsList,
      },
    });
  }
}
